/** First-run structure; the component kit owns every interactive button. */
import { createButton } from './components.js';
import { icon } from './icons.js';

const STAGE_HTML = `
  <div class="pb-stage-bg" aria-hidden="true"></div>
  <div class="pb-stage-dotgrid" aria-hidden="true"></div>
  <div class="pb-stage-spotlight" aria-hidden="true"></div>
  <div class="pb-stage-content">
    <div class="pb-stage-greeting">
      <img class="pb-stage-logo" src="assets/brand/icon-256.png" alt="" aria-hidden="true" width="160" height="160">
      <h1 class="pb-stage-headline">Where do we start?</h1>
      <p class="pb-stage-subtitle">
        <strong class="pb-stage-greeting-prefix">Good evening</strong>
        — choose a starting point. Browse templates, add sections, or import a client’s brand.
      </p>
    </div>

    <div class="pb-stage-doors" role="group" aria-label="Choose how to start">
      <div class="pb-stage-door pb-stage-door-primary" data-door="2" data-testid="stage-door-template">
        <div class="pb-stage-door-art" aria-hidden="true">
          <div class="pb-stage-door-art-deck">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="pb-stage-door-body">
          <span class="pb-stage-door-title">
            Start from a template
          </span>
          <p class="pb-stage-door-desc">Load a polished site for one industry.</p>
        </div>
        <!-- FN-14: the recommendation is STATED, not implied by size. Three
             equally-weighted doors made the fastest route to a finished
             site look like one option of three; visual primacy alone left
             a first-time user to infer which was meant.
             It rides the card's top border rather than trailing the title, so
             it labels the CARD; a direct child of the door because that is the
             box it is positioned against. Last in DOM order so the accessible
             name still opens with the door's own title. -->
        <span class="pb-stage-door-badge">Recommended</span>
      </div>

      <div class="pb-stage-door" data-door="1" data-testid="stage-door-section">
        <div class="pb-stage-door-art" aria-hidden="true">
          <div class="pb-stage-door-art-stack">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="pb-stage-door-body">
          <span class="pb-stage-door-title">
            Pick a section
          </span>
          <p class="pb-stage-door-desc">Browse 1,000+ sections by category and drop one in.</p>
        </div>
      </div>

      <div class="pb-stage-door" data-door="3" data-testid="stage-door-compose" role="group">
        <div class="pb-stage-door-art pb-stage-door-art-url" aria-hidden="false">
          <!-- SW-3 / S3: the URL field is mounted here by createUrlField, so
               the door and the wizard's step 1 ask the question the same way —
               same pill, same leading glyph, same live echo of the address that
               will actually be fetched, same named refusal. The bespoke
               <input> and the .pb-stage-door-url-error line below are gone. -->
          <div
            class="pb-stage-door-signin"
            data-testid="stage-door-signin"
            hidden
          >Sign in to import a brand from URL</div>
        </div>
        <div class="pb-stage-door-body">
          <span class="pb-stage-door-title">
            Import brand from URL
          </span>
          <p class="pb-stage-door-desc">Paste a client URL and press Enter — we’ll apply their brand to the closest template.</p>
        </div>
      </div>
    </div>

    <div
      class="pb-stage-open-project"
      data-testid="stage-open-project"
      hidden
    >
      <span>Or open a saved project</span>
    </div>

  </div>
`;

export function createCanvasEmptyStageMarkup() {
  const template = document.createElement('template');
  template.innerHTML = STAGE_HTML;
  const fragment = template.content;
  for (const testid of [
    'stage-door-template',
    'stage-door-section',
    'stage-door-signin',
    'stage-open-project'
  ]) {
    const placeholder = fragment.querySelector(`[data-testid="${testid}"]`);
    const button = createButton({
      variant: 'bare',
      className: placeholder.className,
      testid,
      dataset: placeholder.dataset.door ? { door: placeholder.dataset.door } : undefined,
      icon: testid === 'stage-open-project' ? icon('projects') : undefined,
      content: [...placeholder.childNodes]
    });
    button.hidden = placeholder.hidden;
    placeholder.replaceWith(button);
  }
  return fragment;
}
